/*  SeoHead — per-page document.title + <meta> injection.
    ────────────────────────────────────────────────────────────────
    iter100.147 — Marketing pages mount `<SeoHead page="product_secure" />`
    at the top. Reads `seo.title`, `seo.description`, `seo.og_image`
    from the same CMS page slug and writes them into document.title
    + <meta name="description"> / <meta property="og:*"> / <link rel=canonical>.

    Cleans up on unmount / page change (best-effort — SPAs don't have a
    true doc lifecycle so we upsert tags in-place).
*/
import { useEffect } from "react";
import { useCms } from "@/lib/useCms";


function upsertMeta(attr, value, content) {
    if (!content) return;
    let el = document.head.querySelector(`meta[${attr}="${value}"]`);
    if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, value);
        document.head.appendChild(el);
    }
    el.setAttribute("content", content);
}


function upsertLink(rel, href) {
    if (!href) return;
    let el = document.head.querySelector(`link[rel="${rel}"]`);
    if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        document.head.appendChild(el);
    }
    el.setAttribute("href", href);
}


export default function SeoHead({ page, fallbackTitle, fallbackDescription, fallbackOgImage }) {
    const { t, loading } = useCms(page);

    useEffect(() => {
        if (loading) return;
        const title       = t("seo.title", fallbackTitle || "");
        const description = t("seo.description", fallbackDescription || "");
        const ogImage     = t("seo.og_image", fallbackOgImage || "");

        if (title) document.title = title;
        if (description) upsertMeta("name", "description", description);
        if (title) upsertMeta("property", "og:title", title);
        if (description) upsertMeta("property", "og:description", description);
        if (ogImage) upsertMeta("property", "og:image", ogImage);
        if (title) upsertMeta("name", "twitter:title", title);
        if (description) upsertMeta("name", "twitter:description", description);
        if (ogImage) upsertMeta("name", "twitter:image", ogImage);
        upsertMeta("name", "twitter:card", ogImage ? "summary_large_image" : "summary");

        // Canonical URL — the exact route the visitor is on.
        upsertLink("canonical", window.location.origin + window.location.pathname);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loading, page, t]);

    return null;
}
